/** @jsxImportSource theme-ui */
import { truncate } from 'lodash';
import React, { useEffect, useState } from 'react';

import { sxtheme } from 'utils/cssHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';

interface TextTruncateProps {
  /** Full text to be truncated */
  value: string;
  /**
   * Number of characters to be displayed before truncation
   * @default 100
   */
  length?: number;
  /**
   * Determines whether the full text will be displayed initially
   * @default false
   */
  isExpanded?: boolean;
}

const TextTruncate: React.FC<TextTruncateProps> = ({ value, length = 100, isExpanded = false }) => {
  const shouldShowIsMore = (value || '').length > length;
  const [isMoreVisible, setIsMoreVisible] = useState(!isExpanded && shouldShowIsMore);

  useEffect(() => setIsMoreVisible(!isExpanded && shouldShowIsMore), [shouldShowIsMore]);

  // eslint-disable-next-line no-irregular-whitespace
  const formattedShortContent = truncate(value || '', { length, separator: /\n,? +/ }).replace(
    /\n/g,
    '<br/>'
  );
  const formattedValue = (value || '').replace(/\n/g, '<br/>');

  return (
    <div
      sx={{
        'ol, ul': {
          paddingInlineStart: '15px',
        },
      }}
    >
      {!shouldShowIsMore && (
        <span
          dangerouslySetInnerHTML={{
            __html: formattedValue,
          }}
        />
      )}
      {shouldShowIsMore && (
        <>
          <Typography variant="body2" color="stone90">
            <div
              dangerouslySetInnerHTML={{
                __html: isMoreVisible ? formattedShortContent : formattedValue,
              }}
            />
          </Typography>
          <div sx={{ mt: '5px' }} onClick={() => setIsMoreVisible(!isMoreVisible)}>
            <Typography
              variant="body2"
              color="sea90"
              sx={{
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': {
                  color: sxtheme(t => t.colors.sea60),
                  textDecoration: 'underline',
                  transition: 'ease .3s',
                },
              }}
            >
              <FlexGroup
                alignItems="center"
                gutter="4px"
                prefix={
                  isMoreVisible ? <BdIcon name="icon-maximize" /> : <BdIcon name="icon-minimize" />
                }
              >
                {isMoreVisible ? 'Show more' : 'Show less'}
              </FlexGroup>
            </Typography>
          </div>
        </>
      )}
    </div>
  );
};

export default TextTruncate;
